import request from '@/modules/base/util/request'
import { isNil, merge, pickBy } from 'lodash'
import expenseConfig from './config'
import { BankTransferFormModel, BankTransferRequestModel } from '@/modules/banking/models'
import globalConfig from '@/config'
import { getList, LIST_TYPES, QUERY_TYPES } from '@/redux/ducks/lists'

const url = `${expenseConfig.searchUrl}`
const defaultOptions = {
  method: 'GET',
  auth: true,
  isConcurrent: true,
}

class ExpenseRequestFactory {
  constructor(searchTerm) {
    // encodeURIComponent
    this._searchTerm = searchTerm || ''
    this._state = 'all'
    this._limit = 0
    this._offset = 0
  }
  attributes(attributes) {
    this._attributes = attributes
    return this
  }
  state(state) {
    this._state = state
    return this
  }
  limit(value) {
    this._limit = value
    return this
  }
  offset(value) {
    this._offset = value
    return this
  }
  send() {
    const data = pickBy({
      term: this._searchTerm,
    }, value => value !== isNil)
    const options = Object.assign({}, defaultOptions, { data })
    return request(url, options)
  }
}

export default class ExpenseService {
  static find(searchTerm) {
    return new ExpenseRequestFactory(searchTerm)
  }

  static syncState(expenseId) {
    return request(`${expenseConfig.urls.resource}/${expenseId}/state/sync`, {
      method: 'PUT',
      auth: true,
    })
  }

  static createBankTransferRequestData(booking) {
    const {
      accountHolder,
      bookingAmount: amount,
      bookingDate: executionDate,
      purposeDescription,
      bankAccountId,
      iban,
      bic,
    } = booking

    return new BankTransferRequestModel({
      accountHolder,
      iban,
      bic,
      executionDate,
      purposeDescription,
      amount,
      bankAccountId,
    })
  }

  static createBankTransferFormData(expense) {
    const {
      receiptNumber: usage,
      outstandingAmount: amount,
      payee,
      data: supplier,
    } = expense

    let accountHolder = payee || ''
    let bic = expense.payeeBic || ''
    let iban = expense.payeeIban || ''

    if (supplier && Array.isArray(supplier.bankAccounts) && supplier.bankAccounts.length > 0) {
      accountHolder = supplier.bankAccounts[0].accountHolder || accountHolder
      bic = supplier.bankAccounts[0].bic || bic
      iban = supplier.bankAccounts[0].iban || iban
    }

    return new BankTransferFormModel({
      amount,
      accountHolder,
      bic,
      iban,
      payee,
      usage,
    })
  }

  static getList(options) {
    return new Promise((resolve, reject) => {
      request(expenseConfig.urls.resource, {
        auth: true,
        data: options,
      })
        .then(result => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static getById(id) {
    return new Promise((resolve, reject) => {
      request(`${expenseConfig.urls.resource}/${id}`, {
        auth: true,
      })
        .then(result => resolve(result.body))
        .catch(reject)
    })
  }

  static getByFinanceApiId(financeApiId, onlyId = false) {
    return new Promise((resolve, reject) => {
      request(`${expenseConfig.urls.resource}/booking/${financeApiId}?onlyId=${onlyId}`, {
        auth: true,
      })
        .then(result => resolve(result.body))
        .catch(reject)
    })
  }

  static copy(id) {
    return new Promise((resolve, reject) => {
      request(`${expenseConfig.urls.resource}/${id}/copy`, {
        auth: true,
      })
        .then(result => resolve(result.body))
        .catch(reject)
    })
  }

  static getListSetting() {
    return new Promise((resolve, reject) => {
      request(expenseConfig.urls.listSetting, { auth: true, isConcurrent: true })
        .then(result => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static updateListSort(data) {
    return new Promise((resolve, reject) => {
      request(expenseConfig.urls.sort, {
        auth: true,
        isConcurrent: true,
        method: 'PUT',
        data,
      })
        .then(resolve)
        .catch(reject)
    })
  }

  static updateListColumns(columns) {
    return new Promise((resolve, reject) => {
      request(expenseConfig.urls.columns, {
        auth: true,
        isConcurrent: true,
        method: 'PUT',
        data: columns,
      })
        .then(resolve)
        .catch(reject)
    })
  }

  static deleteById(id) {
    return new Promise((resolve, reject) => {
      request(`${expenseConfig.urls.resource}/${id}`, {
        auth: true,
        method: 'DELETE',
      })
        .then(resolve)
        .catch(reject)
    })
  }

  static create(data) {
    return new Promise((resolve, reject) => {
      request(`${expenseConfig.urls.resource}`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data,
      })
        .then((result) => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static update(id, data) {
    return new Promise((resolve, reject) => {
      request(`${expenseConfig.urls.resource}/${id}`, {
        auth: true,
        isConcurrent: true,
        timeout: globalConfig.requestExtraLongTimeout,
        method: 'PUT',
        data,
      })
        .then(result => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static updateTags(id, data) {
    return new Promise((resolve, reject) => {
      request(`${expenseConfig.urls.resource}/${id}/tags`, {
        auth: true,
        isConcurrent: true,
        timeout: globalConfig.requestExtraLongTimeout,
        method: 'PUT',
        data,
      })
        .then(result => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static async getPreviousAndNextExpense(id, customFilters) {
    return this.getPreviousAndNextListRow(`expense:${id}`, id, { customFilters })
  }

  // FLAG:useDocuAi — the same neighbour lookup, anchored on ANY list rowKey instead of only on an
  // expense id. The review component (/documents/review/:fileId) is a row of this very list too
  // ('document:<fileId>'), so it can offer the same previous/next navigation instead of being the dead end
  // the walk used to hit: "Nächste Ausgabe" onto a review-pending row left the reviewer with no
  // navigation at all. `fallbackId` keeps the flag-OFF path (no rowKey in the payload) unchanged.
  static async getPreviousAndNextListRow(rowKey, fallbackId, { customFilters = null, requireAnchor = false } = {}) {
    try {
      // get the current sort and filters for the list so the previous and next are equal to the list items
      const expenseListSettingsResponse = await this.getListSetting()
      const { meta: { defaultSort, sortBy } } = expenseListSettingsResponse
      const sortModel = sortBy.length
        ? sortBy.map((sort) => ({ sort: sort.direction, colId: sort.name }))
        : defaultSort.map((sort) => ({ sort: sort.direction, colId: sort.name }))
      const { searchTerm, quickFilter, filterItems } = getList(LIST_TYPES.EXPENSE_LIST_TYPE)
      let filters = {
        filterModel: quickFilter,
        filterItems,
        searchTerm,
        sortModel,
        queryType: QUERY_TYPES.DATA,
      }
      if (customFilters) {
        filters = merge(filters, customFilters)
      }
      const response = await request(expenseConfig.routes.list, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        timeout: globalConfig.requestExtraLongTimeout,
        data: {
          ...filters,
          filterModel: filters.filterModel || {},
        },
      })
      const { body: { data: expenses } } = response

      // FLAG:useDocuAi — this list unions BOOKINGS (rowType 'expense') with still-to-review
      // documents (rowType 'documentReview'), and file ids and expense ids collide — which is
      // exactly why the list carries `rowKey` ('expense:<id>' / 'document:<fileId>'). Matching on
      // the bare id could therefore land on the wrong row type. Flag-OFF lists have no rowKey and
      // no review rows, so the fallback keeps that path byte-identical.
      const currentIndex = expenses.findIndex((row) => (
        row.rowKey ? row.rowKey === rowKey : row.id === fallbackId
      ))
      // FLAG:useDocuAi — a row the current list filter does NOT contain has no neighbours; without
      // this, `expenses[-1 + 1]` hands back the FIRST row of the list as "next". Opt-in, because the
      // legacy expense walk has always lived with that and flag-OFF must stay byte-identical.
      if (requireAnchor && currentIndex < 0) {
        return { previous: undefined, next: undefined }
      }
      // The neighbours are returned as full rows: the caller has to know a review row is NOT an
      // expense, or it navigates to /expenses/edit/<fileId> and the API answers NOT_FOUND.
      return { previous: expenses[currentIndex - 1], next: expenses[currentIndex + 1] }
    } catch (error) {
      throw error
    }
  }

  static async reAnalyze(id) {
    return request(`${expenseConfig.urls.resource}/${id}/reanalyze`, {
      auth: true,
      isConcurrent: true,
      timeout: globalConfig.requestExtraLongTimeout,
      method: 'POST',
    })
  }

  static async reAnalyzeAll() {
    return request(`${expenseConfig.urls.resource}/reanalyze`, {
      auth: true,
      isConcurrent: true,
      timeout: globalConfig.requestExtraLongTimeout,
      method: 'POST',
    })
  }

  static async unlinkBookings(id) {
    return request(`${expenseConfig.urls.resource}/${id}/bookings/unlink`, {
      auth: true,
      isConcurrent: true,
      timeout: globalConfig.requestExtraLongTimeout,
      method: 'POST',
    })
  }

  // FLAG:useDocuAi — „In Prüfung zurücksetzen" from an expense row: the row carries no fileId, so
  // the API resolves it from the receipt link. Puts the reviewed invoice back to the DocuAI analysis
  // state and REMOVES this draft expense; refused for a booked one (EXPENSE_BOOKED).
  static async resetInvoiceReview(id) {
    return request(`${expenseConfig.urls.resource}/${id}/review/reset`, {
      auth: true,
      isConcurrent: true,
      timeout: globalConfig.requestExtraLongTimeout,
      method: 'POST',
    })
  }

  static async deleteExpenseReceipt(id) {
    return request(`${expenseConfig.urls.resource}/receipt/${id}`, {
      auth: true,
      isConcurrent: true,
      timeout: globalConfig.requestExtraLongTimeout,
      method: 'DELETE',
    })
  }

  static async markAsSeen(id) {
    return request(`${expenseConfig.urls.resource}/${id}/markAsSeen`, {
      auth: true,
      isConcurrent: true,
      method: 'PUT',
    })
  }
}
